import { Button, Input, Popconfirm, Space, Table, message } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { useNavigate } from "react-router-dom";
import { EntityForm } from "../../components/EntityForm";
import { useEffect, useMemo, useState } from "react";
import {
  createClient,
  deleteClient,
  getClients,
  updateClient,
} from "../../api/client";

import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { NoPermission } from "../../components/EntityForm/NoPermision/noPermission";
import { getFileUrl } from "../../api/file";
import { UserRoles } from "../user";

export function Client({ user }) {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState(null);
  const dataIndex = (page - 1) * perPage;

  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showCreateClient, setShowCreateClient] = useState(false);
  const [editClient, setEditClient] = useState(null);

  const sections = useMemo(() => {
    const attachments = [];
    const avatars = [];
    if (editClient) {
      attachments.push(
        ...(editClient.attachments || []).map((attachment) => {
          return {
            ...attachment,
            uid: attachment.id,
            name: attachment.originalName,
            status: "done",
            url: getFileUrl(attachment),
          };
        })
      );
      if (editClient.avatar)
        avatars.push({
          ...editClient.avatar,
          uid: editClient.avatar.id,
          name: editClient.avatar.originalName,
          status: "done",
          url: getFileUrl(editClient.avatar),
        });
    }
    return [
      {
        items: [
          {
            type: "avatar",
            label: "Client Profile Picture/Logo (2MB)",
            name: "avatar",
            fields: { clientId: editClient?.id, entity: "client" },
            col: 12,
            maxLen: 200,
            fileList: avatars,
          },
        ],
      },
      {
        items: [
          {
            type: "text",
            label: "Name",
            name: "name",
            required: true,
          },
          {
            type: "text",
            label: "Trading Name",
            name: "tradingName",
            required: false,
          },
          {
            type: "text",
            label: "Phone No",
            name: "phone",
            required: true,
          },
        ],
      },
      {
        items: [
          {
            type: "email",
            label: "Email",
            name: "email",
            required: true,
          },
          {
            type: "text",
            label: "Billing Email",
            name: "billingEmail",
            required: false,
          },
        ],
      },
      {
        items: [
          {
            type: "text",
            label: "Key Contact First Name",
            name: "keyContactFirstName",
            required: true,
          },
          {
            type: "text",
            label: "Key Contact Last Name",
            name: "keyContactLastName",
            required: false,
          },
          {
            type: "email",
            label: "Key contact email",
            name: "keyContactEmail",
            required: false,
          },
        ],
      },
      {
        items: [
          {
            type: "text",
            label: "Address Name",
            name: "addressName",
            required: true,
          },
          {
            type: "text",
            label: "Street",
            name: "street",
            required: false,
          },
          {
            type: "text",
            label: "State",
            name: "state",
            required: false,
          },
          {
            type: "text",
            label: "Country",
            name: "country",
            required: false,
          },
          {
            type: "text",
            label: "Address Line 1",
            name: "addressLine1",
            required: true,
          },
          {
            type: "text",
            label: "Address Line 2",
            name: "addressLine2",
            required: false,
          },
          {
            type: "text",
            label: "Google Id",
            name: "googlePlaceId",
            required: false,
          },
          {
            type: "number",
            label: "Latitude",
            name: "lat",
            col: 4,
            required: false,
          },

          {
            type: "number",
            label: "Logitude",
            name: "long",
            col: 4,
            required: false,
          },
        ],
      },
      {
        items: [
          {
            type: "text-area",
            label: "Description",
            name: "description",
            required: false,
            maxLen: 200,
          },
        ],
      },
      {
        items: [
          {
            additionalInfo: "Client Document, images etc.",
            type: "attachments",
            label: "Attachments (2MB)",
            name: "attachments",
            col: 12,
            maxLen: 200,
            fileList: attachments,
            fields: { clientId: editClient?.id, entity: "client" },
          },
        ],
      },
    ];
  }, [editClient]);

  const fetchClients = async ({ page, q }) => {
    try {
      setIsLoading(true);
      const { total, results } = await getClients({ page, perPage, q });
      setTotal(total);
      setClients(results);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user || ![UserRoles.Admin, UserRoles.User].includes(user.role)) return;
    fetchClients({ page, perPage, q: searchQuery });
  }, [user, page, searchQuery]);

  const handleSubmit = async ({
    addressName,
    street,
    state,
    country,
    addressLine1,
    addressLine2,
    googlePlaceId,
    lat,
    long,
    ...clientData
  }) => {
    const avatar =
      clientData.avatar &&
      clientData.avatar.fileList &&
      clientData.avatar.fileList.length
        ? clientData.avatar.fileList.map((file) => {
            if (!file.response) return file;
            return file.response.file;
          })[0]
        : clientData.avatar;

    const attachments =
      clientData.attachments && clientData.attachments.fileList
        ? clientData.attachments.fileList.map((file) => {
            if (!file.response) return file;
            return file.response.file;
          })
        : clientData.attachments;

    const address = {
      name: addressName,
      street,
      state,
      country,
      addressLine1,
      addressLine2,
      googlePlaceId,
      lat,
      long,
    };

    if (editClient && editClient.address) {
      address.id = editClient.address.id;
    }

    if (editClient) {
      await updateClient({
        ...clientData,
        attachments,
        avatar,
        address,
        id: editClient.id,
      });
    } else {
      await createClient({ ...clientData, address, attachments, avatar });
    }
    setEditClient(null);
    setShowCreateClient(false);
    fetchClients({ page, perPage });
  };

  const handleDelete = async (client) => {
    await deleteClient(client.id);
    fetchClients(page, perPage);
  };

  const TableCols = [
    { title: "No", dataIndex: "no", key: "no", width: 80 },
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Action",
      key: "action",
      render: (text, record, index) => (
        <Space size={2}>
          <Popconfirm
            title="Delete the task"
            description="Are you sure to delete this client?"
            onConfirm={() => handleDelete(clients[index])}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} danger></Button>
          </Popconfirm>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => setEditClient(clients[index])}
          ></Button>
        </Space>
      ),
    },
  ];

  if (user && ![UserRoles.Admin, UserRoles.User].includes(user.role))
    return <NoPermission role={user.role} />;

  return (
    <Space direction="vertical" style={{ width: "100%" }}  className="p-6">
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <PageHeader
          onBack={() => navigate(-1)}
          title="Clients"
          subTitle="List of clients in company"
        />

        <Button type="primary" onClick={() => setShowCreateClient(true)}>
          Create Client
        </Button>
      </Space>

      <Space>
        <Input.Search
          className="search"
          placeholder="Name"
          onSearch={(str) => {
            setSearchQuery(str);
            setPage(1);
          }}
        />
      </Space>

      <Table
        dataSource={clients.map((client, index) => {
          return {
            no: dataIndex + index + 1,
            ...client,
          };
        })}
        columns={TableCols}
        loading={isLoading}
        pagination={{
          size: "small",
          total,
          pageSize: 5,
          current: parseInt(page),
          onChange: setPage,
          showQuickJumper: true,
          showTotal: () => `Total ${total} clients`,
          position: ["bottomLeft"],
        }}
      />

      {(showCreateClient || editClient) && (
        <EntityForm
          initialValues={{
            ...editClient,
            addressName: editClient?.address?.name,
            street: editClient?.address?.street,
            state: editClient?.address?.state,
            country: editClient?.address?.country,
            addressLine1: editClient?.address?.addressLine1,
            addressLine2: editClient?.address?.addressLine2,
            googlePlaceId: editClient?.address?.googlePlaceId,
            lat: editClient?.address?.lat,
            long: editClient?.address?.long,
          }}
          sections={sections}
          title="Create Client"
          open={showCreateClient || editClient}
          onCancel={() => {
            setShowCreateClient(false);
            setEditClient(null);
          }}
          onSubmit={handleSubmit}
          onDelete={() =>
            handleDelete(editClient).then(() => setEditClient(null))
          }
          isEditMode={!!editClient}
        ></EntityForm>
      )}
    </Space>
  );
}
